import { gql, TypedDocumentNode } from 'urql';
import {
  IAddTruckLoadItemsInput,
  IContItemsResponse,
  IContItemsWhere,
  ICreatePreScheduleResponse,
  ICreateTruckLoadInputByGraphQL,
  IGetTruckLoad,
  IGetTruckLoadsResponse,
  IGetVehicles,
  ILoadTruckLoadItemsInput,
  IPreScheduleInput,
  ITruckLoadRules,
  ITruckLoadRulesWhere,
  IUpdateTruckLoadInputByGraphQL,
} from '../interfaces/TruckLoad';

const TruckLoadsQueries = {
  getTruckLoads: (): TypedDocumentNode<
    { truckLoads: IGetTruckLoadsResponse[] },
    { where: { organization_id?: string } }
  > => gql`
    query GetTruckLoads($where: TruckLoadWhereInput!) {
      truckLoads: GetTruckLoads(where: $where) {
        id
        loadedAmount: loaded_amount
        status
        name
        expiresAt: expires_at
        createdAt: created_at
        loadingOrder: loading_order {
          loadingOrder: loading_order
        }
        pickUpLocation: pick_up_location {
          title
        }
        driver {
          name
        }
        cif_cadence {
          startTime: start_time
        }
        fob_cadence {
          startTime: start_time
        }
        farm_cadence {
          startTime: start_time
        }
        vehicle {
          vehicleModel: vehicle_model
        }
        truckLoadItems: truck_load_items {
          contractItem: contract_item {
            contract {
              farm {
                name
              }
            }
          }
        }
      }
    }
  `,
  downloadLoadingOrder: (): TypedDocumentNode<
    { pdf: { url: string } },
    { where: { truck_load_id: string } }
  > => gql`
    query downloadLoadingOrder($where: GetLoadingOrderPdfWhere!) {
      pdf: GetLoadingOrderPdf(where: $where) {
        url: pdf_url
      }
    }
  `,
  cancelTruckLoad: (): TypedDocumentNode<
    { truckLoad: { id: string; status: string } },
    { where: { id: string } }
  > => gql`
    mutation cancelTruckLoad($where: TruckLoadWhereUniqueInput!) {
      cancel: CancelTruckLoad(where: $where) {
        id
        status
      }
    }
  `,
  createPreSchedule: (): TypedDocumentNode<
    { preSchedule: ICreatePreScheduleResponse },
    { data: IPreScheduleInput }
  > => gql`
    mutation createPreSchedule($data: PreScheduleUncheckedCreateInput!) {
      preSchedule: CreatePreSchedule(data: $data) {
        id
        cif_cadence {
          rule {
            capacityPerCompany: capacity_per_company
          }
        }
        fob_cadence {
          rule {
            capacityPerCompany: capacity_per_company
          }
        }
        farm_cadence {
          farm_rule {
            capacityPerCompany: capacity
          }
        }
        vehicle {
          id
          vehicleModel: vehicle_model
          bagsCapacity: bags_capacity
        }
        createdBy: created_by
        startedAt: started_at
        expiresIn: expires_in
        unitsAmount: units_amount
        pickUpLocation: pick_up_location {
          organizationId: organization_id
        }
      }
    }
  `,
  listTruckLoadRules: (): TypedDocumentNode<
    { rule: ITruckLoadRules[] },
    { where: ITruckLoadRulesWhere }
  > => gql`
    query listLoadsRules($where: RuleWhereInput!) {
      rule: ListRules(where: $where) {
        id
        unity
        seedType: seed_type {
          nameWithoutAccent: name_without_accent
          name
        }
        pickUpLocation: pick_up_location {
          id
          title
        }
        cif_cadences {
          id
          customerCapacityPerDay: farm_capacity_per_day {
            remainingAmount: remaining_amount
          }
          capacityPerDay: capacity_per_day
          availableUnits: available_units
          startTime: start_time
          endTime: end_time
        }
        fob_cadences {
          id
          customerCapacityPerDay: farm_capacity_per_day {
            remainingAmount: remaining_amount
          }
          capacityPerDay: capacity_per_day
          availableUnits: available_units
          startTime: start_time
          endTime: end_time
        }
      }
    }
  `,
  getVehicles: (): TypedDocumentNode<
    { vehicles: IGetVehicles[] },
    {
      where: {
        organization_id: { equals: string };
      };
    }
  > => gql`
    query ListVehicles($where: VehicleWhereInput!) {
      vehicles: ListVehicles(where: $where) {
        id
        vehicleModel: vehicle_model
        bagsCapacity: bags_capacity
        weightCapacity: weight_capacity
      }
    }
  `,
  listContractItems: (): TypedDocumentNode<
    { list: { contractItems: IContItemsResponse[] } },
    { where: IContItemsWhere }
  > => gql`
    query listContractItems($where: ListContractItemsInput!) {
      list: ListContractItems(where: $where) {
        contractItems: contract_items {
          id
          cultivation
          sieve
          category
          packing
          tsi
          estimatedWeight: estimated_weight
          customerCapacity: customer_capacity {
            remainingAmount: remaining_amount
          }
          contract {
            referenceNumber: reference_number
            contractDate: contract_date
            farm {
              name
              stateRegistration: state_registration
            }
          }
          fullAmount: full_amount
          remainingAmount: remaining_amount
        }
      }
    }
  `,

  cancelPreSchedule: (): TypedDocumentNode<
    { cancel: { success: boolean } },
    { where: { id: string } }
  > => gql`
    mutation CancelPreSchedule($where: PreScheduleWhereUniqueInput!) {
      cancel: CancelPreSchedule(where: $where) {
        success
      }
    }
  `,

  createTruckLoad: (): TypedDocumentNode<
    { truckLoad: { id: string } },
    { input: ICreateTruckLoadInputByGraphQL }
  > => gql`
    mutation formTruckLoad($input: TruckLoadCreateInput!) {
      truckLoad: FormTruckLoad(input: $input) {
        id
      }
    }
  `,

  getPreScheduleByCreator: (): TypedDocumentNode<
    { getPreScheduleByCreator: Array<{ id: string; created_by: string }> },
    {
      where: {
        created_by: {
          equals: string;
        };
        expired: {
          equals: false;
        };
      };
    }
  > => gql`
    query getPreSchedule($where: PreScheduleWhereInput!) {
      getPreScheduleByCreator: ListPreSchedules(where: $where) {
        id
        createdBy: created_by
      }
    }
  `,

  authorizeTruckLoad: (): TypedDocumentNode<
    { authorizeTruckLoad: { id: string; authorizedAt: string } },
    { where: { id: string } }
  > => gql`
    mutation AuthorizeTruckLoad($where: TruckLoadWhereUniqueInput!) {
      authorizeTruckLoad: AuthorizeTruckLoad(where: $where) {
        id
        authorizedAt: authorized_at
      }
    }
  `,

  fetchTruckLoad: (): TypedDocumentNode<
    { getTruckLoad: IGetTruckLoad },
    { where: { id: string } }
  > => gql`
    query FindTruckLoad($where: TruckLoadWhereUniqueInput!) {
      getTruckLoad: FindTruckLoad(where: $where) {
        id
        name
        status
        allocatedAmount: allocated_amount
        loadedAmount: loaded_amount
        noShippingCompany: no_shipping_company
        preScheduleId: pre_schedule_id
        loadNote: load_note
        vehicle {
          id
          vehicleModel: vehicle_model
          bagsCapacity: bags_capacity
        }
        pickUpLocation: pick_up_location {
          title
        }
        cif_cadence {
          startTime: start_time
        }
        fob_cadence {
          startTime: start_time
        }
        farm_cadence {
          startTime: start_time
        }
        seedType: seed_type {
          name
          nameWithoutAccent: name_without_accent
        }
        truckLoadItems: truck_load_items {
          id
          allocatedAmount: allocated_amount
          contractItem: contract_item {
            id
            cultivation
            sieve
            category
            packing
            tsi
            estimatedWeight: estimated_weight
            contract {
              referenceNumber: reference_number
              contractDate: contract_date
              farm {
                name
                stateRegistration: state_registration
              }
            }
            fullAmount: full_amount
            remainingAmount: remaining_amount
          }
        }
        shippingCompany: shipping_company {
          id
          documentNumberCnpj: document_number_cnpj
          tradeName: trade_name
          companyName: company_name
          email: email
          phoneNumber: phone_number
        }
        driver {
          name
          documentNumberCpf: document_number_cpf
          phoneNumber: phone_number
          vehicleModel: vehicle_model
          vehicleLicensePlate: vehicle_license_plate
          note
        }
        authorization {
          userName: user_name
          userCpf: user_cpf
          userPhone: user_phone
          userEmail: user_email
        }
      }
    }
  `,

  updateTruckLoad: (): TypedDocumentNode<
    { updateTruckLoad: { id: string } },
    { data: IUpdateTruckLoadInputByGraphQL; where: { id: string } }
  > => gql`
    mutation updateTruckLoad(
      $data: TruckLoadUpdateInput!
      $where: TruckLoadWhereUniqueInput!
    ) {
      updateTruckLoad: UpdateTruckLoad(data: $data, where: $where) {
        id
      }
    }
  `,

  addTruckLoadItems: (): TypedDocumentNode<
    { addTruckLoadItems: { id: string } },
    {
      input: IAddTruckLoadItemsInput;
      where: {
        id: string;
      };
    }
  > => gql`
    mutation addTruckLoadItems(
      $input: TruckLoadItemCreateManyInputEnvelope!
      $where: TruckLoadWhereUniqueInput!
    ) {
      addTruckLoadItems: AddTruckLoadItems(input: $input, where: $where) {
        id
      }
    }
  `,

  deleteTruckLoadItems: (): TypedDocumentNode<
    { deleteItems: { success: boolean } },
    { input: { data: { id: string } } }
  > => gql`
    mutation delete($input: DeleteTruckLoadItemInput!) {
      deleteItems: DeleteTruckLoadItems(input: $input) {
        success
      }
    }
  `,

  loadTruckLoadItems: (): TypedDocumentNode<
    { updateTruckLoad: { id: string } },
    { input: ILoadTruckLoadItemsInput }
  > => gql`
    mutation loadTruckLoadItems($input: TruckLoadItemLoadManyWithWhereInput!) {
      loadTruckLoadItems: LoadTruckLoadItems(input: $input) {
        id
      }
    }
  `,
};

export default TruckLoadsQueries;
